'use server'

import { apiRequest, ApiResponse } from '../request'

export type EstablishmentType =
    | 'CLINIC'
    | 'HOSPITAL'
    | 'GYM'
    | 'LABORATORY'
    | 'PHARMACY'
    | 'REHABILITATION_CENTER'
    | 'SPA'
    | 'DENTAL_CLINIC'
    | 'VETERINARY'
    | 'OTHER'

export const ESTABLISHMENT_TYPE_LABELS: Record<EstablishmentType, string> = {
    CLINIC: 'Clínica',
    HOSPITAL: 'Hospital',
    GYM: 'Academia',
    LABORATORY: 'Laboratório',
    PHARMACY: 'Farmácia',
    REHABILITATION_CENTER: 'Centro de Reabilitação',
    SPA: 'Spa',
    DENTAL_CLINIC: 'Clínica Odontológica',
    VETERINARY: 'Veterinária',
    OTHER: 'Outro',
}

export interface EstablishmentSpecialtyItem {
    id: number
    specialtyId: number
    specialty: {
        id: number
        name: string
        parentId?: number | null
        parent?: { id: number; name: string } | null
    }
}

export interface EstablishmentServiceItem {
    id: number
    medicalServiceId: number
    medicalService: {
        id: number
        name: string
        code?: string | null
    }
}

export interface Establishment {
    id: number
    name: string
    tradingName?: string | null
    cnpj?: string | null
    type: EstablishmentType
    logoUrl?: string | null
    email?: string | null
    phone1?: string | null
    phone2?: string | null
    phone3?: string | null
    notes?: string | null
    street?: string | null
    number?: string | null
    complement?: string | null
    neighborhood?: string | null
    city?: string | null
    state?: string | null
    zipCode?: string | null
    country?: string | null
    organizationId: number
    active: boolean
    specialties: EstablishmentSpecialtyItem[]
    services: EstablishmentServiceItem[]
    createdAt: string
    updatedAt: string
}

export interface CreateEstablishmentData {
    name: string
    organizationId: number
    type: EstablishmentType
    tradingName?: string
    cnpj?: string
    email?: string
    phone1?: string
    phone2?: string
    phone3?: string
    notes?: string
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
    active?: boolean
    specialtyIds?: number[]
    serviceIds?: number[]
}

export interface UpdateEstablishmentData extends Partial<CreateEstablishmentData> {}

// ==================== CRUD ====================

export async function getEstablishmentsByOrganization(
    organizationId: number,
    params?: {
        activeOnly?: boolean
        active?: boolean
        page?: number
        limit?: number
        search?: string
        type?: EstablishmentType
        sortBy?: string
        sortOrder?: string
    }
): Promise<ApiResponse<Establishment[]>> {
    const queryParams: Record<string, any> = {}
    if (params?.activeOnly) queryParams.activeOnly = 'true'
    if (typeof params?.active === 'boolean') queryParams.active = params.active ? 'true' : 'false'
    if (params?.page) queryParams.page = params.page
    if (params?.limit) queryParams.limit = params.limit
    if (params?.search) queryParams.search = params.search
    if (params?.type) queryParams.type = params.type
    if (params?.sortBy) queryParams.sortBy = params.sortBy
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder
    return apiRequest<Establishment[]>(`establishments/organization/${organizationId}`, { params: queryParams })
}

export async function getEstablishment(id: number): Promise<ApiResponse<Establishment>> {
    return apiRequest<Establishment>(`establishments/${id}`, {})
}

export async function createEstablishment(data: CreateEstablishmentData): Promise<ApiResponse<Establishment>> {
    return apiRequest<Establishment>('establishments', { data }, 'post')
}

export async function updateEstablishment(id: number, data: UpdateEstablishmentData): Promise<ApiResponse<Establishment>> {
    return apiRequest<Establishment>(`establishments/${id}`, { data }, 'patch')
}

export async function uploadEstablishmentLogo(
    id: number,
    file: File
): Promise<ApiResponse<{ id: number; name: string; tradingName: string | null; logoUrl: string }>> {
    const formData = new FormData()
    formData.append('logo', file)
    return apiRequest<{ id: number; name: string; tradingName: string | null; logoUrl: string }>(
        `establishments/${id}/logo`,
        { data: formData, formData: true },
        'post'
    )
}

export async function toggleEstablishmentActive(id: number): Promise<ApiResponse<Establishment>> {
    return apiRequest<Establishment>(`establishments/${id}/toggle-active`, {}, 'patch')
}

export async function deleteEstablishment(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`establishments/${id}`, {}, 'delete')
}
